import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { Button } from '@/components/ui/button'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { cartQueryKeys, useIsCartMutating } from '@/lib/services/carts/carts.query'
import { useRestartSalesOrderChangeMutation } from '@/lib/services/sales-orders/sales-order-changes.query'
import { getErrorMessage } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Trash2 } from 'lucide-react'
import { toast } from 'sonner'

type DiscardOrderChangeButtonProps = {
  salesOrderId: number
  cartId: number
  /** A draft that changes nothing has nothing to discard. */
  hasChanges: boolean
  /** The draft's controls allow clearing it (`can_delete`): not while a payment toward the change is open on the order. */
  canDiscard: boolean
}

/** Starts a change again from the order as it is, discarding this draft and staying on the edit screen. */
export function useRestartOrderChange(salesOrderId: number, cartId: number) {
  const navigate = useNavigate()
  const path = usePathHelper()
  const queryClient = useQueryClient()
  const organizationId = useRouteOrganizationId()
  const restart = useRestartSalesOrderChangeMutation()

  const start = async () => {
    try {
      // Naming this draft makes a repeated confirm return the new draft instead of discarding it too.
      const response = await restart.mutateAsync({ salesOrderId, cartId })
      await navigate({
        ...path.salesOrders.edit(salesOrderId).search({ cartId: response.cart_id }).link,
        replace: true,
      })
    } catch (error) {
      toast.error('Failed to discard changes', {
        description: getErrorMessage(error) || 'Please try again',
      })
      // An order that can no longer change still clears the draft it refused to restart: read it again so the screen
      // shows that the change is closed.
      await queryClient.invalidateQueries({
        queryKey: cartQueryKeys.posShow({ organizationId, cartId }),
      })
    }
  }

  return { start, isPending: restart.isPending }
}

/** Clears a change to an order and starts again from the order as it is, staying on the edit screen. */
export function DiscardOrderChangeButton({
  salesOrderId,
  cartId,
  hasChanges,
  canDiscard,
}: DiscardOrderChangeButtonProps) {
  const isCartMutating = useIsCartMutating()
  const restart = useRestartOrderChange(salesOrderId, cartId)
  const discard = restart.start

  return (
    <ConfirmationDialog
      variant="destructive"
      title="Discard changes?"
      description="Your changes are cleared and the order’s items come back as they are on the order."
      onConfirm={discard}
      isPending={restart.isPending}
      pendingText="Discarding…"
      confirmText="Discard Changes"
      trigger={
        <Button
          variant="outline"
          size="sm"
          disabled={!hasChanges || !canDiscard || isCartMutating > 0 || restart.isPending}
        >
          <Trash2 className="size-4" aria-hidden="true" />
          Discard Changes
        </Button>
      }
    />
  )
}
